/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { BooleanRadioButtonGroup, BooleanRadioButton, Callout } from "@octopusdeploy/design-system-components";
import { ParseHelper } from "@octopusdeploy/design-system-octopus-components";
import { AccountType, CommunicationStyleValues } from "@octopusdeploy/octopus-server-client";
import type { SshEndpointResource, ProxyResource, AccountResource } from "@octopusdeploy/octopus-server-client";
import { EndpointRegistrationKeyValues } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { SshConnection } from "~/components/Images/MachineSettings/SshConnection";
import ExternalLink from "~/components/Navigation/ExternalLink";
import { Text, ExpandableFormSection, FormSectionHeading, Summary, Select, Note } from "~/components/form";
import AccountSelect from "~/components/form/AccountSelect/AccountSelect";
import CommonSummaryHelper from "~/utils/CommonSummaryHelper";
import { EndpointCard } from "./EndpointCard";
import LinuxCateogry from "./LinuxCategoryDefinition";
import MacCategory from "./MacCategoryDefinition";
import type { MachineRegistration } from "./endpointRegistry";
import styles from "./styles.module.less";
interface SshEndpointProps {
    endpoint: SshEndpointResource;
    proxies: ProxyResource[];
    accounts: AccountResource[];
    refreshAccounts: () => Promise<{}>;
    onChange(newValue: SshEndpointResource): void;
}
interface SshEndpointState {
    shouldUseProxy: boolean;
}
class SshEndpoint extends React.Component<SshEndpointProps, SshEndpointState> {
    public static dotnetCorePlatforms = ["linux-x64", "osx-x64", "linux-arm", "linux-arm64"];
    public static experimentalPlatforms = ["experimental"];
    public static defaultDotnetCorePlatform = "linux-x64";
    constructor(props: SshEndpointProps) {
        super(props);
        this.state = {
            shouldUseProxy: !!this.props.endpoint.ProxyId,
        };
    }
    render() {
        return (<div>
                <ExpandableFormSection errorKey="Account" title="Account" focusOnExpandAll summary={CommonSummaryHelper.resourceSummary(this.props.endpoint.AccountId, this.props.accounts, "account")} help="Account">
                    <AccountSelect onRequestRefresh={this.props.refreshAccounts} value={this.props.endpoint.AccountId} type={[AccountType.UsernamePassword, AccountType.SshKeyPair]} allowClear={true} onChange={(x) => {
                const endpoint = this.props.endpoint;
                endpoint.AccountId = x;
                this.props.onChange(endpoint);
            }} items={this.props.accounts}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Host" title="Host" summary={this.props.endpoint.Host ? Summary.summary(this.props.endpoint.Host) : Summary.placeholder("No host")} help="The hostname or IP address of the deployment target to connect to.">
                    <Text label="Host" placeholder="hostname.com or 10.0.0.1" value={this.props.endpoint.Host} onChange={(x) => {
                const endpoint = this.props.endpoint;
                endpoint.Host = x;
                this.props.onChange(endpoint);
            }} type="url"/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Port" title="Port" summary={CommonSummaryHelper.portSummary(this.props.endpoint.Port)} help="Enter a port number.">
                    <Text label="Port" placeholder="The port to use when connecting to the remote host." value={this.props.endpoint.Port ? this.props.endpoint.Port.toString() : ""} onChange={(x) => {
                const endpoint = this.props.endpoint;
                endpoint.Port = ParseHelper.safeParseInt(x);
                this.props.onChange(endpoint);
            }} type="number"/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Fingerprint" title="Fingerprint" summary={this.props.endpoint.Fingerprint ? Summary.summary(this.props.endpoint.Fingerprint) : Summary.placeholder("No fingerprint")} help="Enter the host fingerprint to be verified.">
                    <Text label="Fingerprint" placeholder="SHA256:xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={this.props.endpoint.Fingerprint} onChange={(x) => {
                const endpoint = this.props.endpoint;
                endpoint.Fingerprint = x;
                this.props.onChange(endpoint);
            }}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="Proxy" title="Proxy" summary={CommonSummaryHelper.resourceSummary(this.props.endpoint.ProxyId, this.props.proxies, "proxy")} help="Select whether to use a proxy to communicate with this SSH target.">
                    <BooleanRadioButtonGroup title="Connection method" onChange={(shouldUseProxy) => {
                this.setState({ shouldUseProxy });
                const endpoint = this.props.endpoint;
                endpoint.ProxyId = undefined;
                this.props.onChange(endpoint);
            }} value={this.state.shouldUseProxy}>
                        <BooleanRadioButton value={false} label="Connect directly" isDefault={true}/>
                        <BooleanRadioButton value={true} label="Connect through a proxy server"/>
                    </BooleanRadioButtonGroup>
                    {this.state.shouldUseProxy && (<Select label="The proxy to use to communicate with this SSH endpoint." onChange={(x) => {
                    const endpoint = this.props.endpoint;
                    endpoint.ProxyId = x;
                    this.props.onChange(endpoint);
                }} value={this.props.endpoint.ProxyId} items={this.props.proxies.map((p) => ({ value: p.Id, text: p.Name }))}/>)}
                </ExpandableFormSection>

                <FormSectionHeading title=".NET"/>
                <ExpandableFormSection errorKey="Platform" title="Platform" summary={this.getDotnetCorePlatformSummary()} help="Select the platform">
                    <Select label="Platform" onChange={(x) => {
                const endpoint = this.props.endpoint;
                endpoint.DotNetCorePlatform = x;
                this.props.onChange(endpoint);
            }} value={this.props.endpoint.DotNetCorePlatform || SshEndpoint.defaultDotnetCorePlatform} items={SshEndpoint.dotnetCorePlatforms.map((p) => ({ value: p, text: p }))}/>
                    {this.props.endpoint && this.isExperimentalPlatform(this.props.endpoint.DotNetCorePlatform) && (<Callout type={"warning"} title="Experimental">
                            <b>{this.props.endpoint.DotNetCorePlatform}</b> support is currently experimental.
                            <br />
                            <ExternalLink href="ExperimentalSshTargetsFeedbackForm">Share with us</ExternalLink> your experience using this feature.
                            <br />
                            <i>
                                Let us know why you use <b>{this.props.endpoint.DotNetCorePlatform}</b>, and if there are other platforms you would like to see supported.
                            </i>
                        </Callout>)}
                    <Note>
                        <ExternalLink href="Calamari">Calamari</ExternalLink> (the Octopus deployment executable) is built on Microsoft .NET. This option ensures that a platform-specific, self-contained version of Calamari will be used.
                    </Note>
                </ExpandableFormSection>
            </div>);
    }
    private getDotnetCorePlatformSummary() {
        if (!this.props.endpoint || !this.props.endpoint.DotNetCorePlatform) {
            return Summary.default(SshEndpoint.defaultDotnetCorePlatform);
        }
        const isExperimentalMessage = this.isExperimentalPlatform(this.props.endpoint.DotNetCorePlatform) ? " (experimental)" : "";
        return Summary.summary(this.props.endpoint.DotNetCorePlatform + isExperimentalMessage);
    }
    private isExperimentalPlatform(platform: string | undefined) {
        return platform && SshEndpoint.experimentalPlatforms && SshEndpoint.experimentalPlatforms.indexOf(platform) > -1;
    }
    static displayName = "SshEndpoint";
}
export default SshEndpoint;
const sshEndpointRegistration: MachineRegistration = {
    key: EndpointRegistrationKeyValues.Ssh,
    displayOrder: 50,
    communicationStyle: CommunicationStyleValues.Ssh,
    name: "SSH Connection",
    discoverable: true,
    categories: [LinuxCateogry, MacCategory],
    renderCard: ({ registration, onNavigate, onClick }) => (<EndpointCard logo={<SshConnection className={styles.centreThumbnail} title={registration.name}/>} registrationName={registration.name} description="Connect to this target via Secure Shell (SSH)." onNavigate={onNavigate} onClick={onClick} communicationStyle={registration.communicationStyle}/>),
};
export { sshEndpointRegistration };
